import React, { useState } from 'react';
import {
  ChevronRight, Check, Car, Search, FileText,
  Phone, TrendingUp, Shield, MapPin, ChevronDown, ChevronUp
} from 'lucide-react';
import '../styles/HowItWorks.css';
import { useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────
   SVG hero illustration  (car + person + floats)
───────────────────────────────────────────── */
function HeroIllustration() {
  return (
    <svg
      className="hiw-hero__svg"
      viewBox="0 0 640 400"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <filter id="hiw-drop" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="rgba(0,0,0,0.22)" />
        </filter>
        <filter id="hiw-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="0" stdDeviation="10" floodColor="#FFD60A" floodOpacity="0.6" />
        </filter>
        <radialGradient id="hiw-ground" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#6aabff" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#6aabff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="hiw-car-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#daeeff" />
          <stop offset="100%" stopColor="#b8d8f0" />
        </linearGradient>
        <linearGradient id="hiw-car-top" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#eaf5ff" />
          <stop offset="100%" stopColor="#c8e4f8" />
        </linearGradient>
      </defs>

      {/* ground glow */}
      <ellipse cx="310" cy="342" rx="220" ry="26" fill="url(#hiw-ground)" />

      {/* ── CONCENTRIC RINGS behind car ── */}
      <circle cx="310" cy="255" r="155" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1.5" />
      <circle cx="310" cy="255" r="195" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />

      {/* ── CAR ── */}
      {/* body */}
      <rect x="112" y="240" width="360" height="74" rx="14" fill="url(#hiw-car-body)" />
      {/* cabin */}
      <path d="M190 240 Q210 172 278 166 L368 166 Q418 166 438 240 Z" fill="url(#hiw-car-top)" />
      {/* windshield */}
      <path d="M202 240 Q218 186 272 180 L358 180 Q398 182 424 240 Z" fill="#a8d8f0" opacity="0.75" />
      {/* rear glass */}
      <path d="M278 180 L282 170 L358 170 L368 180 Z" fill="#92c8e8" opacity="0.55" />
      {/* hood */}
      <path d="M112 278 Q112 252 142 246 L192 242 L192 314 L112 314 Z" fill="#c2dcee" />
      {/* trunk */}
      <path d="M438 242 L472 246 Q492 252 492 278 L492 314 L438 314 Z" fill="#c2dcee" />
      {/* under stripe */}
      <rect x="112" y="304" width="360" height="12" rx="4" fill="#a0c8e0" />
      {/* door line */}
      <line x1="310" y1="244" x2="310" y2="314" stroke="rgba(0,53,102,0.12)" strokeWidth="1.5" />
      {/* door handles */}
      <rect x="256" y="272" width="20" height="6" rx="3" fill="rgba(0,53,102,0.18)" />
      <rect x="334" y="272" width="20" height="6" rx="3" fill="rgba(0,53,102,0.18)" />
      {/* plate */}
      <rect x="282" y="306" width="56" height="11" rx="3" fill="#FFD60A" />
      <text x="310" y="315" textAnchor="middle" fontSize="7.5" fontWeight="800" fill="#003566">Caryugo</text>
      {/* headlights */}
      <rect x="472" y="256" width="22" height="12" rx="5" fill="#FFD60A" opacity="0.9" />
      <rect x="474" y="272" width="14" height="7" rx="3.5" fill="#FFD60A" opacity="0.55" />
      {/* tail light */}
      <rect x="110" y="258" width="16" height="12" rx="4" fill="#ff6b6b" opacity="0.85" />

      {/* ── WHEELS ── */}
      {[196, 420].map(cx => (
        <g key={cx}>
          <circle cx={cx} cy="318" r="34" fill="#1c1c30" />
          <circle cx={cx} cy="318" r="24" fill="#2a3160" />
          <circle cx={cx} cy="318" r="14" fill="#ccdce8" />
          <circle cx={cx} cy="318" r="5"  fill="#FFD60A" />
          {[0,60,120,180,240,300].map(a => (
            <line key={a}
              x1={cx + 16*Math.cos(a*Math.PI/180)} y1={318 + 16*Math.sin(a*Math.PI/180)}
              x2={cx + 24*Math.cos(a*Math.PI/180)} y2={318 + 24*Math.sin(a*Math.PI/180)}
              stroke="#ccdce8" strokeWidth="2.5" strokeLinecap="round"
            />
          ))}
        </g>
      ))}

      {/* ── PERSON ── */}
      {/* shadow */}
      <ellipse cx="558" cy="344" rx="28" ry="7" fill="rgba(0,0,0,0.16)" />
      {/* legs */}
      <rect x="541" y="294" width="12" height="48" rx="6" fill="#1e3a7a" />
      <rect x="558" y="294" width="12" height="48" rx="6" fill="#1e3a7a" />
      {/* shoes */}
      <ellipse cx="547" cy="342" rx="10" ry="5" fill="#111" />
      <ellipse cx="564" cy="342" rx="10" ry="5" fill="#111" />
      {/* body jacket */}
      <rect x="533" y="224" width="46" height="74" rx="11" fill="#FFD60A" />
      {/* collar detail */}
      <path d="M546 224 L556 244 L546 244 Z" fill="white" opacity="0.35" />
      <path d="M566 224 L556 244 L566 244 Z" fill="white" opacity="0.35" />
      {/* shirt */}
      <rect x="546" y="242" width="20" height="28" rx="4" fill="white" />
      {/* left arm holding clipboard */}
      <rect x="512" y="244" width="22" height="12" rx="6" fill="#FFD60A" />
      {/* right arm raised/pointing */}
      <rect x="578" y="230" width="12" height="32" rx="6" fill="#FFD60A" />
      {/* pointing hand */}
      <circle cx="584" cy="228" r="7" fill="#f0a060" />
      {/* clipboard */}
      <rect x="494" y="244" width="22" height="30" rx="4" fill="#e8f4fc" filter="url(#hiw-drop)" />
      <rect x="502" y="240" width="8" height="7" rx="2" fill="#aaa" />
      <line x1="498" y1="256" x2="512" y2="256" stroke="#003566" strokeWidth="1.5" />
      <line x1="498" y1="263" x2="512" y2="263" stroke="#003566" strokeWidth="1.5" />
      <line x1="498" y1="270" x2="508" y2="270" stroke="#003566" strokeWidth="1.5" />
      {/* head */}
      <circle cx="556" cy="207" r="22" fill="#f0a060" />
      {/* hair */}
      <path d="M536 200 Q538 182 556 180 Q574 182 576 200 Q572 190 556 188 Q540 190 536 200 Z" fill="#2a1408" />
      {/* eyes */}
      <ellipse cx="549" cy="208" rx="3" ry="3.5" fill="white" />
      <ellipse cx="563" cy="208" rx="3" ry="3.5" fill="white" />
      <circle cx="550" cy="208" r="2" fill="#111" />
      <circle cx="564" cy="208" r="2" fill="#111" />
      {/* smile */}
      <path d="M549 218 Q556 224 563 218" stroke="#c07030" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* ears */}
      <ellipse cx="534" cy="208" rx="4" ry="5" fill="#e09050" />
      <ellipse cx="578" cy="208" rx="4" ry="5" fill="#e09050" />

      {/* ── SHIELD BADGE (top-right floating) ── */}
      <g className="hiw-svg-float-a">
        <rect x="556" y="44" width="66" height="72" rx="15" fill="white" filter="url(#hiw-drop)" />
        <path d="M570 70 Q589 59 608 70 L608 99 Q589 110 570 99 Z" fill="#FFD60A" filter="url(#hiw-glow)" />
        <path d="M576 82 L584 92 L602 75"
          stroke="#003566" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </g>

      {/* ── COIN / MONEY BADGE (left floating) ── */}
      <g className="hiw-svg-float-b">
        <rect x="44" y="172" width="66" height="66" rx="15" fill="white" filter="url(#hiw-drop)" />
        <circle cx="77" cy="205" r="21" fill="#FFD60A" filter="url(#hiw-glow)" />
        <text x="77" y="213" textAnchor="middle" fontSize="20" fontWeight="900" fill="#003566">₹</text>
      </g>

      {/* ── SPARKLE STARS ── */}
      <g className="hiw-svg-sparkle-a">
        <path d="M90 118 L94 104 L98 118 L112 122 L98 126 L94 140 L90 126 L76 122 Z" fill="#FFD60A" />
      </g>
      <g className="hiw-svg-sparkle-b">
        <path d="M564 142 L567 133 L570 142 L579 145 L570 148 L567 157 L564 148 L555 145 Z" fill="#FFD60A" />
      </g>
      <g className="hiw-svg-sparkle-a" style={{animationDelay:'-1s'}}>
        <path d="M500 80 L502 74 L504 80 L510 82 L504 84 L502 90 L500 84 L494 82 Z" fill="#FFD60A" opacity="0.7" />
      </g>
      <circle className="hiw-svg-sparkle-b" cx="136" cy="196" r="5" fill="#FFD60A" opacity="0.55" />
      <circle className="hiw-svg-sparkle-a" cx="598" cy="195" r="4" fill="#FFD60A" opacity="0.45" />
      <circle className="hiw-svg-sparkle-b" cx="94"  cy="282" r="3" fill="white"   opacity="0.35" />
      <circle className="hiw-svg-sparkle-a" cx="536" cy="124" r="5" fill="#FFD60A" opacity="0.35" />

      {/* ── CONFETTI triangles ── */}
      <polygon points="26,34  38,34  32,52"  fill="#FFD60A" opacity="0.85" />
      <polygon points="48,14  60,14  54,32"  fill="#003566" opacity="0.45" />
      <polygon points="580,14 592,14 586,32" fill="#FFD60A" opacity="0.85" />
      <polygon points="602,34 614,34 608,52" fill="#003566" opacity="0.45" />
      <polygon points="618,56 630,56 624,74" fill="#FFD60A" opacity="0.6"  />
      <polygon points="10,58  22,58  16,76"  fill="#FFD60A" opacity="0.5"  />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════
   PAGE COMPONENT
═══════════════════════════════════════════════════ */
export default function HowItWorks() {
  const [activeTab, setActiveTab] = useState('buying');
  const [openFaq, setOpenFaq]     = useState(null);
  const navigate = useNavigate();

  const buyingSteps = [
    {
      icon: <Search size={26} />,
      title: 'Browse Verified Cars',
      description: 'Explore inspected and verified cars listed on Caryugo Cars.',
      details: [
        'View real photos and complete car details',
        'Each car goes through a basic verification process',
        'Filter cars by budget, brand and fuel type',
      ],
    },
    {
      icon: <Car size={26} />,
      title: 'Book Test Drive',
      description: 'Schedule a test drive at your preferred time and location.',
      details: [
        'Choose available time slots easily',
        'Test drive arranged by our team',
        'No pressure to purchase',
      ],
    },
    {
      icon: <Shield size={26} />,
      title: 'Reserve the Car',
      description: 'Reserve the car by paying a small token amount.',
      details: [
        'Block the car instantly',
        'Avoid missing out on your chosen vehicle',
        'Fully transparent process',
      ],
    },
    {
      icon: <FileText size={26} />,
      title: 'Payment & Documentation',
      description: 'Complete payment and let us handle paperwork.',
      details: [
        'Secure payment options available',
        'RC transfer handled by our team',
        'Complete documentation support',
      ],
    },
    {
      icon: <MapPin size={26} />,
      title: 'Get Home Delivery',
      description: 'Receive your car at your doorstep or pickup location.',
      details: [
        'Hassle-free delivery experience',
        'Final checks before handover',
        'Support available post delivery',
      ],
    },
  ];

  const sellingSteps = [
    {
      icon: <Phone size={26} />,
      title: 'Enter Car Details',
      description: 'Submit your car details in a simple listing form.',
      details: [
        'Provide basic car information',
        'Upload images of your car',
        'Takes only a few minutes',
      ],
    },
    {
      icon: <TrendingUp size={26} />,
      title: 'Get Price Estimate',
      description: 'Receive an estimated price based on your car details.',
      details: [
        'Smart pricing based on market data',
        'No need to negotiate manually',
        'Transparent valuation process',
      ],
    },
    {
      icon: <Shield size={26} />,
      title: 'Schedule Inspection',
      description: 'Book a car inspection at your preferred location.',
      details: [
        'Inspection by our team',
        'Condition verification',
        'Improves final price accuracy',
      ],
    },
    {
      icon: <Check size={26} />,
      title: 'Final Price Offer',
      description: 'Get a final price after inspection approval.',
      details: [
        'Fair and transparent offer',
        'No hidden deductions',
        'You can accept or reject the offer',
      ],
    },
    {
      icon: <FileText size={26} />,
      title: 'Instant Payment',
      description: 'Receive payment after documentation is completed.',
      details: [
        'Fast and secure payment process',
        'RC transfer handled by us',
        'Hassle-free car pickup',
      ],
    },
  ];

  const whyChoose = [
    { icon: '🔍', title: 'Verified Cars',       description: 'All cars go through a verification process to ensure basic quality and trust.' },
    { icon: '💰', title: 'Transparent Pricing', description: 'Clear pricing with no hidden charges or unexpected costs.' },
    { icon: '🛠️', title: 'End-to-End Support',  description: 'From inspection to RC transfer, we manage the entire process for you.' },
    { icon: '🔐', title: 'Safe Transactions',   description: 'Secure payments and controlled process for both buyers and sellers.' },
    { icon: '📍', title: 'Local Service',        description: 'Focused on Indore and nearby cities for better support and faster service.' },
  ];

  const faqs = [
    { q: 'Is car inspection mandatory?',          a: 'Inspection is recommended for accurate pricing and faster selling. It helps us verify the car condition before listing or finalizing the deal.' },
    { q: 'Can I return the car after buying?',    a: 'Return policies may vary. We recommend checking the return option details on the car listing before making a purchase.' },
    { q: 'How is the car price decided?',         a: 'Pricing is based on market data, car condition, and inspection results. Sellers do not need to negotiate with buyers.' },
    { q: 'How long does it take to sell my car?', a: 'It depends on demand and car condition. After inspection and approval, the process is usually quick and smooth.' },
    { q: 'Who handles RC transfer?',              a: 'Our team assists with complete RC transfer and documentation to ensure a smooth ownership change.' },
  ];

  const currentSteps = activeTab === 'buying' ? buyingSteps : sellingSteps;

  const scrollToSteps = (tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      document.getElementById('hiw-steps-anchor')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  return (
    <div className="hiw-page">

      {/* ══════════ HERO ══════════ */}
      <section className="hiw-hero">
        <div className="hiw-hero__dots" aria-hidden="true" />

        {/* confetti corners */}
        <div className="hiw-confetti hiw-confetti--left"  aria-hidden="true">
          {[...Array(5)].map((_,i)=><span key={i} className={`hcf hcf-${i}`} />)}
        </div>
        <div className="hiw-confetti hiw-confetti--right" aria-hidden="true">
          {[...Array(5)].map((_,i)=><span key={i} className={`hcf hcf-${i}`} />)}
        </div>

        <div className="hiw-hero__left">
          <span className="hiw-hero__tag">HOW IT WORKS</span>
          <h1 className="hiw-hero__title">Buy or Sell a Car —</h1>
          <p className="hiw-hero__sub">
            Caryugo Cars manages the complete car buying and selling journey — from inspection to payment and delivery.
          </p>
          <div className="hiw-hero__btns">
            <button className="hiw-hero__btn hiw-hero__btn--gold"    onClick={() => scrollToSteps('buying')}>
              <Car size={18} /> Buying a Car
            </button>
            <button className="hiw-hero__btn hiw-hero__btn--outline" onClick={() => scrollToSteps('selling')}>
              <TrendingUp size={18} /> Selling a Car
            </button>
          </div>
        </div>

        <div className="hiw-hero__right">
          <HeroIllustration />
        </div>
      </section>

      {/* ══════════ WORKS STRIP ══════════ */}
      <div className="hiw-works-strip">
        <svg className="hiw-works-strip__wave" viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,60 L0,22 Q360,64 720,20 Q1080,-24 1440,22 L1440,60 Z" fill="#f0f4fa" />
        </svg>
        <div className="hiw-works-strip__label">
          <span className="hiw-works-strip__rule" />
          <span className="hiw-works-strip__text">How Caryugo Works</span>
          <span className="hiw-works-strip__rule" />
        </div>
      </div>

      {/* ══════════ TABS ══════════ */}
      <div className="hiw-tabs-wrap" id="hiw-steps-anchor">
        <div className="hiw-tabs">
          <button className={`hiw-tab ${activeTab==='buying'  ? 'hiw-tab--active':''}`} onClick={()=>setActiveTab('buying')}>
            <Car size={17}/> Buying a Car
          </button>
          <button className={`hiw-tab ${activeTab==='selling' ? 'hiw-tab--active':''}`} onClick={()=>setActiveTab('selling')}>
            <TrendingUp size={17}/> Selling a Car
          </button>
        </div>
      </div>

      {/* ══════════ STEPS ══════════ */}
      <section className="hiw-steps">
        <div className="hiw-steps__track">
          <div className="hiw-steps__connector" aria-hidden="true" />
          {currentSteps.map((step, idx) => (
            <div key={idx} className="hiw-step-card">
              <div className="hiw-step-card__num">{String(idx+1).padStart(2,'0')}</div>
              <div className="hiw-step-card__icon-row">
                <div className="hiw-step-card__icon-wrap">{step.icon}</div>
                {idx < currentSteps.length - 1 && (
                  <div className="hiw-step-card__arrow"><ChevronRight size={14}/></div>
                )}
              </div>
              <h3 className="hiw-step-card__title">{step.title}</h3>
              <p  className="hiw-step-card__desc">{step.description}</p>
              <ul className="hiw-step-card__list">
                {step.details.map((d,i) => (
                  <li key={i}><span className="hiw-check"><Check size={11}/></span>{d}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════ WHY CHOOSE ══════════ */}
      <section className="hiw-why">
        <div className="hiw-why__inner">
          <p  className="hiw-section-eyebrow">Why Caryugo Cars</p>
          <h2 className="hiw-section-title">What Makes Us Different</h2>
          <div className="hiw-why__grid">
            {whyChoose.map((item,i) => (
              <div key={i} className="hiw-why-card">
                <div className="hiw-why-card__icon">{item.icon}</div>
                <h3 className="hiw-why-card__title">{item.title}</h3>
                <p  className="hiw-why-card__desc">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FAQ ══════════ */}
      <section className="hiw-faq">
        <div className="hiw-faq__inner">
          <h2 className="hiw-section-title">Frequently Asked Questions</h2>
          <div className="hiw-faq__list">
            {faqs.map((faq,i) => (
              <div key={i}
                className={`hiw-faq__item ${openFaq===i?'hiw-faq__item--open':''}`}
                onClick={()=>setOpenFaq(openFaq===i?null:i)}
              >
                <div className="hiw-faq__question">
                  <span>{faq.q}</span>
                  {openFaq===i ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
                </div>
                {openFaq===i && <p className="hiw-faq__answer">{faq.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CTA ══════════ */}
 {/*     <section className="hiw-cta">
        <div className="hiw-cta__dots" aria-hidden="true" />
        <div className="hiw-cta__inner">
          <h2 className="hiw-cta__title">Ready to Get Started?</h2>
          <p  className="hiw-cta__sub">Browse cars or sell your car with a simple and guided process.</p>
          <div className="hiw-cta__btns">
            <button className="hiw-cta__btn hiw-cta__btn--primary"   onClick={()=>navigate('/buy-cars')}>Browse Cars</button>
            <button className="hiw-cta__btn hiw-cta__btn--secondary" onClick={()=>navigate('/sell-car')}>Sell Your Car</button>
          </div>
        </div>
      </section>*/}

    </div>
  );
}